import React, { useEffect, useState } from "react"

import {
    IPatientIdentificationData,
    IPolicyIdentificationData,
} from "@common-shared/types/policies.types"
import { IOneVerifyPolicyComment } from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { BrowserStorageType } from "@front-shared/config/general.config"
import { BrowserStorageHelper } from "@front-shared/helpers/browserStorage.helper"

import { GENERAL_CONFIG } from "../../../config/general.config"
import {
    PolicySidebarTab,
    PolicySidebarViewMode,
} from "../../../config/policies.config"
import { PolicySidebarCommentsComponent } from "./comments/policySidebarComments.component"
import { PolicySidebarHistoryComponent } from "./history/policySidebarHistory.component"
import { PolicySidebarHeaderComponent } from "./policySidebarHeader.component"

export const PolicySidebarComponent: React.FunctionComponent<
    IPolicySidebarComponentProps
> = props => {
    const [viewMode, setViewMode] = useState<PolicySidebarViewMode>(
        BrowserStorageHelper.get<PolicySidebarViewMode>(
            GENERAL_CONFIG.browserStorageKeys.policySidebarViewMode,
            undefined,
            BrowserStorageType.sessionStorage,
        ) || PolicySidebarViewMode.DEFAULT,
    )

    const [selectedTab, setSelectedTab] = useState<PolicySidebarTab>(
        PolicySidebarTab.HISTORY,
    )

    // Save view mode to session storage, except for expanded mode
    useEffect(() => {
        if (viewMode === PolicySidebarViewMode.EXPANDED) {
            return
        }

        BrowserStorageHelper.set(
            GENERAL_CONFIG.browserStorageKeys.policySidebarViewMode,
            viewMode,
            BrowserStorageType.sessionStorage,
        )
    }, [viewMode])

    return (
        <div
            className={`
                relative overflow-visible md:w-auto
                transition-[width] duration-500 md:transition-none
                ${props.className}
                ${
                    viewMode === PolicySidebarViewMode.CLOSED
                        ? "w-[32px]" // 32px is the width of the icon (10px) plus padding (11px)
                        : "w-[300px]"
                }
            `}
        >
            <div
                className={`
                    absolute z-10 right-0 top-0 h-full flex flex-col
                    bg-brand-warmLight2 overflow-x-hidden overflow-y-auto
                    border-l border-solid border-brand-warmLight
                    transition-[width] duration-500 md:transition-none
                    md:h-[600px] md:w-full
                    ${
                        viewMode === PolicySidebarViewMode.EXPANDED
                            ? "w-[700px]"
                            : "w-full"
                    }
                `}
            >
                <PolicySidebarHeaderComponent
                    viewMode={viewMode}
                    setViewMode={setViewMode}
                    selectedTab={selectedTab}
                    setSelectedTab={setSelectedTab}
                    hideCommentsTab={!props.policy}
                />

                <PolicySidebarHistoryComponent
                    viewMode={viewMode}
                    isHistoryTabOpen={selectedTab === PolicySidebarTab.HISTORY}
                />

                {props.policy ? (
                    <PolicySidebarCommentsComponent
                        viewMode={viewMode}
                        isCommentsTabOpen={
                            selectedTab === PolicySidebarTab.COMMENTS
                        }
                        policy={props.policy}
                        patient={props.patient}
                        onCommentAdded={props.onCommentAdded}
                    />
                ) : null}
            </div>
        </div>
    )
}

interface IPolicySidebarComponentProps {
    className?: string
    policy: IPolicyIdentificationData | null
    patient?: IPatientIdentificationData
    onCommentAdded: (newComment: IOneVerifyPolicyComment) => void
}
